import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Pressable,
  StyleSheet,
  Platform,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import {
  formatDateForLocaleDisplay,
  isoFromParts,
  localTodayIsoDate,
  normalizeIsoDateString,
  parseIsoToParts,
} from '../utils/dateFormat';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function buildMonthCells(year, monthIndex) {
  const firstWeekday = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function mergePickedDateIntoValue(originalValue, isoDate, preserveTime) {
  if (!isoDate) return null;
  if (!preserveTime) return isoDate;

  const original = originalValue != null && originalValue !== '' ? new Date(originalValue) : null;
  if (original && !Number.isNaN(original.getTime())) {
    const parts = parseIsoToParts(isoDate);
    if (!parts) return isoDate;
    const merged = new Date(original);
    merged.setFullYear(parts.year, parts.monthIndex, parts.day);
    return merged.toISOString();
  }
  return `${isoDate}T12:00:00.000Z`;
}

export default function RecordDatePicker({
  value,
  onChange,
  theme,
  isUSUser = false,
  preserveTime = false,
  allowClear = true,
}) {
  const [open, setOpen] = useState(false);
  const selectedIso = normalizeIsoDateString(value);
  const todayIso = localTodayIsoDate();
  const initial = parseIsoToParts(selectedIso) || parseIsoToParts(todayIso);
  const [viewYear, setViewYear] = useState(initial?.year ?? new Date().getFullYear());
  const [viewMonthIndex, setViewMonthIndex] = useState(
    initial?.monthIndex ?? new Date().getMonth()
  );

  useEffect(() => {
    if (!open) return;
    const parts = parseIsoToParts(selectedIso) || parseIsoToParts(todayIso);
    if (parts) {
      setViewYear(parts.year);
      setViewMonthIndex(parts.monthIndex);
    }
  }, [open, selectedIso, todayIso]);

  const cells = useMemo(
    () => buildMonthCells(viewYear, viewMonthIndex),
    [viewYear, viewMonthIndex]
  );

  const textColor = theme.isDarkMode ? '#FFFFFF' : '#111111';
  const borderColor = theme.isDarkMode ? '#555555' : '#CCCCCC';
  const surface = theme.isDarkMode ? '#1a1a1a' : '#FFFFFF';
  const gold = theme.colors.primary || theme.colors.heading || '#d4af37';
  const displayLabel = value
    ? formatDateForLocaleDisplay(value, isUSUser)
    : 'Pick date';

  const goPrevMonth = () => {
    if (viewMonthIndex === 0) {
      setViewMonthIndex(11);
      setViewYear((y) => y - 1);
      return;
    }
    setViewMonthIndex((m) => m - 1);
  };

  const goNextMonth = () => {
    if (viewMonthIndex === 11) {
      setViewMonthIndex(0);
      setViewYear((y) => y + 1);
      return;
    }
    setViewMonthIndex((m) => m + 1);
  };

  const handlePickDay = (day) => {
    if (!day) return;
    const iso = isoFromParts(viewYear, viewMonthIndex, day);
    onChange(mergePickedDateIntoValue(value, iso, preserveTime));
    setOpen(false);
  };

  return (
    <View style={styles.wrap}>
      <TouchableOpacity
        style={[styles.trigger, { borderColor, backgroundColor: surface }]}
        onPress={() => setOpen(true)}
        {...(Platform.OS === 'web'
          ? { dataSet: { recordCellEditor: 'date-trigger' } }
          : {})}
      >
        <MaterialIcons name="event" size={16} color={gold} />
        <Text style={[styles.triggerText, { color: textColor }]} numberOfLines={1}>
          {displayLabel}
        </Text>
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <View style={styles.overlay}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => setOpen(false)}
            accessibilityLabel="Close date picker"
          />
          <View
            style={[styles.sheet, { backgroundColor: surface, borderColor }]}
            {...(Platform.OS === 'web'
              ? { dataSet: { recordDatePicker: 'sheet' } }
              : {})}
          >
            <Text style={[styles.title, { color: textColor }]}>Select date</Text>

            <View style={styles.monthNavRow}>
              <TouchableOpacity style={styles.monthNavBtn} onPress={goPrevMonth}>
                <MaterialIcons name="chevron-left" size={24} color={gold} />
              </TouchableOpacity>
              <Text style={[styles.monthLabel, { color: textColor }]}>
                {MONTH_NAMES[viewMonthIndex]} {viewYear}
              </Text>
              <TouchableOpacity style={styles.monthNavBtn} onPress={goNextMonth}>
                <MaterialIcons name="chevron-right" size={24} color={gold} />
              </TouchableOpacity>
            </View>

            <View style={styles.weekdayRow}>
              {WEEKDAY_LABELS.map((label) => (
                <Text key={label} style={[styles.weekdayLabel, { color: textColor }]}>
                  {label}
                </Text>
              ))}
            </View>

            <View style={styles.daysGrid}>
              {cells.map((day, index) => {
                if (day == null) {
                  return <View key={`empty-${index}`} style={styles.dayCellEmpty} />;
                }
                const iso = isoFromParts(viewYear, viewMonthIndex, day);
                const isToday = iso === todayIso;
                const isSelected = iso === selectedIso;
                return (
                  <TouchableOpacity
                    key={iso}
                    style={[
                      styles.dayCell,
                      isSelected && { backgroundColor: gold },
                      isToday && !isSelected && styles.dayCellToday,
                    ]}
                    onPress={() => handlePickDay(day)}
                  >
                    <Text
                      style={[
                        styles.dayCellText,
                        { color: isSelected ? '#000000' : textColor },
                      ]}
                    >
                      {day}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={styles.footerRow}>
              {allowClear ? (
                <TouchableOpacity
                  style={styles.footerBtn}
                  onPress={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  <Text style={[styles.footerBtnText, { color: textColor }]}>Clear</Text>
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity style={styles.footerBtn} onPress={() => setOpen(false)}>
                <Text style={[styles.footerBtnText, { color: textColor }]}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    width: '100%',
    backgroundColor: 'transparent',
  },
  trigger: {
    minHeight: 32,
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  triggerText: {
    flex: 1,
    fontSize: 13,
    backgroundColor: 'transparent',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  sheet: {
    width: '100%',
    maxWidth: 320,
    borderRadius: 10,
    borderWidth: 1,
    padding: 12,
    zIndex: 2,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 8,
    backgroundColor: 'transparent',
  },
  monthNavRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    backgroundColor: 'transparent',
  },
  monthNavBtn: {
    padding: 4,
    backgroundColor: 'transparent',
  },
  monthLabel: {
    fontSize: 14,
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  weekdayRow: {
    flexDirection: 'row',
    marginBottom: 4,
    backgroundColor: 'transparent',
  },
  weekdayLabel: {
    flex: 1,
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '600',
    opacity: 0.75,
    backgroundColor: 'transparent',
  },
  daysGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: 'transparent',
  },
  dayCellEmpty: {
    width: `${100 / 7}%`,
    aspectRatio: 1,
  },
  dayCell: {
    width: `${100 / 7}%`,
    aspectRatio: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: 'transparent',
  },
  dayCellToday: {
    borderWidth: 1,
    borderColor: '#d4af37',
  },
  dayCellText: {
    fontSize: 13,
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 10,
    backgroundColor: 'transparent',
  },
  footerBtn: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: 'transparent',
  },
  footerBtnText: {
    fontSize: 14,
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
});
